/**
 * AdminSocialAnalyticsPage
 *
 * Visualizes the referral funnel + platform ROI captured by
 * `POST /api/referrals/track-event` and `POST /api/social/share-completed`.
 *
 * Reads `GET /api/admin/analytics/social` which returns:
 *   { funnel, total_shares, total_referral_revenue_usd, top_platforms, top_tours }
 *
 * Design intent: this is an OPS page, not a marketing dashboard. Left-
 * aligned, dense, high signal-to-ink, glanceable at a red-eye o'clock.
 * No pie charts. Numbers first, visualization second.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Share2, DollarSign, Users, Link2, MousePointerClick,
  ShoppingCart, RefreshCw, TrendingUp, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_LABELS = {
  native_share: 'Native (system sheet)',
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  instagram: 'Instagram',
  x: 'X (Twitter)',
  twitter: 'X (Twitter)',
  linkedin: 'LinkedIn',
  messenger: 'Messenger',
  telegram: 'Telegram',
  email: 'Email',
  sms: 'SMS',
  copy: 'Copy link',
};

const nicePlatform = (p) => PLATFORM_LABELS[p] || (p || '—').replace(/_/g, ' ');

const StatTile = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div
    className={`rounded-2xl border p-4 sm:p-5 ${accent || 'bg-white border-slate-200'}`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-[0.65rem] uppercase tracking-widest text-slate-500 font-black">{label}</span>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <div className="text-3xl font-black text-slate-900 leading-none">{value}</div>
    {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
  </div>
);

/**
 * Renders a funnel bar chart where every row's width scales relative to the
 * top-of-funnel count. Uses tabular numerals for glanceability.
 */
const FunnelChart = ({ funnel }) => {
  const rows = useMemo(() => [
    { key: 'clicks', label: 'Link clicks', value: funnel.clicks || 0, icon: MousePointerClick, color: 'bg-teal-500' },
    { key: 'views', label: 'Tour views', value: funnel.views || 0, icon: Link2, color: 'bg-cyan-500' },
    { key: 'starts', label: 'Tours started', value: funnel.starts || 0, icon: TrendingUp, color: 'bg-amber-500' },
    { key: 'purchases', label: 'Purchases', value: funnel.purchases || 0, icon: ShoppingCart, color: 'bg-emerald-500' },
    { key: 'signups', label: 'Accounts created', value: funnel.signups || 0, icon: Users, color: 'bg-fuchsia-500' },
  ], [funnel]);
  const top = Math.max(...rows.map(r => r.value), 1);

  return (
    <div className="space-y-2.5" data-testid="social-funnel">
      {rows.map((r) => {
        const pct = (r.value / top) * 100;
        const conv = rows[0].value > 0 ? ((r.value / rows[0].value) * 100).toFixed(1) : '—';
        return (
          <div key={r.key} className="grid grid-cols-[110px_1fr_auto] items-center gap-3" data-testid={`funnel-row-${r.key}`}>
            <div className="flex items-center gap-2 min-w-0">
              <r.icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-xs text-slate-700 font-semibold truncate">{r.label}</span>
            </div>
            <div className="relative h-7 rounded-lg bg-slate-100 overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${r.color} transition-all duration-700 ease-out`}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2 text-[0.7rem] font-black text-white mix-blend-difference">
                {r.value.toLocaleString()}
              </div>
            </div>
            <span className="text-[0.7rem] tabular-nums text-slate-500 font-mono w-12 text-right">{conv}%</span>
          </div>
        );
      })}
    </div>
  );
};

const PlatformRow = ({ p, rank }) => (
  <div className="grid grid-cols-[24px_1fr_auto_auto_auto] gap-3 items-center py-2.5 border-b border-slate-100 last:border-0" data-testid={`platform-row-${p.platform}`}>
    <span className={`text-xs font-black ${rank === 1 ? 'text-amber-500' : rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-orange-400' : 'text-slate-300'}`}>
      #{rank}
    </span>
    <span className="font-semibold text-sm text-slate-900 truncate">{nicePlatform(p.platform)}</span>
    <span className="text-xs text-slate-500 tabular-nums" title="clicks">{p.clicks} clicks</span>
    <span className="text-xs text-slate-500 tabular-nums" title="purchases">{p.purchases} sales</span>
    <span className="text-sm font-black text-emerald-600 tabular-nums">${p.revenue_usd.toFixed(2)}</span>
  </div>
);

const TourRow = ({ t, rank, tourTitleMap }) => (
  <div className="grid grid-cols-[24px_1fr_auto_auto] gap-3 items-center py-2.5 border-b border-slate-100 last:border-0" data-testid={`tour-row-${t.tour_id}`}>
    <span className={`text-xs font-black ${rank === 1 ? 'text-amber-500' : rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-orange-400' : 'text-slate-300'}`}>
      #{rank}
    </span>
    <span className="font-semibold text-sm text-slate-900 truncate" title={t.tour_id}>
      {tourTitleMap[t.tour_id] || t.tour_id.slice(0, 8) + '…'}
    </span>
    <span className="text-xs text-slate-500 tabular-nums">{t.purchases} sale{t.purchases === 1 ? '' : 's'}</span>
    <span className="text-sm font-black text-emerald-600 tabular-nums">${t.revenue.toFixed(2)}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <h2 className="text-slate-900 font-black text-lg leading-tight">{title}</h2>
      {sub ? <p className="text-xs text-slate-500 mt-0.5">{sub}</p> : null}
    </div>
  </div>
);

const AdminSocialAnalyticsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [tourTitleMap, setTourTitleMap] = useState({});
  // "7" | "30" | "all"  — restricts the funnel/platform/tour aggregates to the
  // last N days at the API layer. `total_shares` is always all-time.
  const [range, setRange] = useState('all');

  const fetchAll = async (rangeArg) => {
    if (!token) return;
    setRefreshing(true);
    try {
      const daysParam = (rangeArg ?? range);
      const [socialRes, toursRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/social`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: daysParam },
        }),
        // Fetch tour titles so the top_tours list is readable, not UUIDs.
        axios.get(`${API}/admin/tours`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setData(socialRes.data);
      const map = {};
      (toursRes.data || []).forEach((t) => { if (t.tour_id) map[t.tour_id] = t.title; });
      setTourTitleMap(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load social analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [token]);

  const changeRange = (next) => {
    setRange(next);
    fetchAll(next);
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 text-sm animate-pulse">Loading social analytics…</div>
      </div>
    );
  }

  const funnel = data?.funnel || {};
  const topPlatforms = (data?.top_platforms || []).slice(0, 3);
  const topTours = (data?.top_tours || []).slice(0, 3);
  const totalShares = data?.total_shares || 0;
  const totalRevenue = data?.total_referral_revenue_usd || 0;
  const clickToPurchase = funnel.clicks > 0
    ? ((funnel.purchases / funnel.clicks) * 100).toFixed(1)
    : '—';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-social-analytics-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              data-testid="social-back-button"
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-700" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Social ROI</h1>
              <p className="text-sm text-slate-500">
                Every Share button, every referral click — the funnel from tap to tour purchase.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Date-range toggle: 7d / 30d / All time */}
            <div
              className="inline-flex rounded-lg border border-slate-300 bg-white overflow-hidden"
              data-testid="social-range-toggle"
            >
              {[
                { key: '7', label: '7d' },
                { key: '30', label: '30d' },
                { key: 'all', label: 'All' },
              ].map((opt) => {
                const active = range === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => changeRange(opt.key)}
                    disabled={refreshing}
                    data-testid={`social-range-${opt.key}`}
                    className={`px-3 py-2 text-xs font-black transition-colors ${
                      active
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => fetchAll()}
              disabled={refreshing}
              data-testid="social-refresh-button"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-white transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatTile
            icon={Share2}
            label="Total shares"
            value={totalShares.toLocaleString()}
            sub="Sum of share_count across completions"
            testid="stat-total-shares"
            accent="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200"
          />
          <StatTile
            icon={MousePointerClick}
            label="Referral clicks"
            value={(funnel.clicks || 0).toLocaleString()}
            sub={`${funnel.views || 0} views · ${funnel.starts || 0} starts`}
            testid="stat-clicks"
          />
          <StatTile
            icon={ShoppingCart}
            label="Attributed purchases"
            value={(funnel.purchases || 0).toLocaleString()}
            sub={`${clickToPurchase}% click → sale`}
            testid="stat-purchases"
            accent="bg-gradient-to-br from-emerald-50 to-lime-50 border-emerald-200"
          />
          <StatTile
            icon={DollarSign}
            label="Referral revenue"
            value={`$${totalRevenue.toFixed(2)}`}
            sub={`${funnel.signups || 0} account signups`}
            testid="stat-revenue"
            accent="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
          />
        </div>

        {/* Funnel */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 mb-6">
          <SectionHeader
            icon={TrendingUp}
            title="Referral funnel"
            sub="Row width scales to the top-of-funnel step. Right column = conversion vs clicks."
          />
          <FunnelChart funnel={funnel} />
        </div>

        {/* Two-column: platforms + tours */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="top-platforms-card">
            <SectionHeader
              icon={Trophy}
              title="Top 3 sharing platforms"
              sub="Ranked by attributed revenue"
            />
            {topPlatforms.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                No platform data yet. Share buttons will populate this table.
              </p>
            ) : (
              <div>
                <div className="grid grid-cols-[24px_1fr_auto_auto_auto] gap-3 pb-2 text-[0.65rem] uppercase tracking-widest text-slate-400 font-black border-b border-slate-200">
                  <span></span>
                  <span>Platform</span>
                  <span>Clicks</span>
                  <span>Sales</span>
                  <span>Revenue</span>
                </div>
                {topPlatforms.map((p, i) => <PlatformRow key={p.platform} p={p} rank={i + 1} />)}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="top-tours-card">
            <SectionHeader
              icon={Trophy}
              title="Top 3 tours by referral revenue"
              sub="Which tours convert best from shares"
            />
            {topTours.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                No referral-attributed purchases yet.
              </p>
            ) : (
              <div>
                <div className="grid grid-cols-[24px_1fr_auto_auto] gap-3 pb-2 text-[0.65rem] uppercase tracking-widest text-slate-400 font-black border-b border-slate-200">
                  <span></span>
                  <span>Tour</span>
                  <span>Sales</span>
                  <span>Revenue</span>
                </div>
                {topTours.map((t, i) => <TourRow key={t.tour_id} t={t} rank={i + 1} tourTitleMap={tourTitleMap} />)}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          Showing{' '}
          <span className="font-black text-slate-600" data-testid="social-range-label">
            {range === 'all' ? 'all-time' : `last ${range} days`}
          </span>
          {' · '}Powered by <span className="font-mono text-slate-500">GET /api/admin/analytics/social?days={range}</span>
          {' · '}
          Attribution is best-effort — anonymous <code>?ref=</code> clicks are captured; purchases need the user to keep the same browser to attribute.
        </p>
      </div>
    </div>
  );
};

export default AdminSocialAnalyticsPage;
